import {html} from 'lit';
import {customElement} from 'lit/decorators.js';
import {ImmutableComponent} from '#lit/components/mixins/immutable_component/index.js';
import type {AbstractConstructor} from '#lit/types/classes.js';
import type {Model} from './architecture.js';
import {Msg, main} from './architecture.js';
import styles from './style.scss';
import type {ExportActivitySummary} from '#lit/types/audience_exports.js';
import {DataTableComponent} from '#lit/components/abstract/data_table/index.js';
import {applyRelevancy, RelevancyWeights} from '#lit/utils/fuzzysearch.js';
import type {CustomInputEvent} from '#lit/components/base/input/index.js';
import {Variant as FlyInputVariant} from '#lit/components/base/input/index.js';
import {classMap} from 'lit/directives/class-map.js';
import {repeat} from 'lit/directives/repeat.js';
import {exportActivitiesLocale} from '#lit/common/locale/export_activities.js';
import type {CustomSelectEvent} from '#lit/components/base/select/index.js';
import {SelectVariant} from '#lit/components/base/select/index.js';
import {fuzzyMatch, safeToGrep} from '#lit/utils/strings.js';
import {paginate} from '#lit/utils/arrays.js';
import type {AudienceExportActivitiesResponse} from '#lit/services/audience_exports.js';
import {audienceExportsService} from '#lit/services/audience_exports.js';
import {renderNotification} from '#lit/utils/notifications.js';
import {AlertIntent, AlertSize} from '#lit/components/base/alert/index.js';
import copy from 'copy-to-clipboard';
import {formatTimestamp} from '#utils/dates.js';

export const AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG =
  'audience-export-activities-table';

const SKELETON_ROW_COUNT = 5;

/**
 * Export Audience Activities table
 */
@customElement(AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG)
export default class AudienceExportActivitiesTable extends ImmutableComponent<
  AbstractConstructor<DataTableComponent<ExportActivitySummary>>,
  Model,
  Msg
>({component: DataTableComponent, main}) {
  static get styles() {
    return [styles, DataTableComponent.styles];
  }

  haystack(exportActivity: ExportActivitySummary): string {
    return (
      applyRelevancy(RelevancyWeights.HIGHEST, exportActivity.audience_name) +
      applyRelevancy(RelevancyWeights.HIGH, exportActivity.destination_name) +
      applyRelevancy(RelevancyWeights.MEDIUM, exportActivity.export_state.state)
    );
  }

  get isFiltered() {
    return (
      (Object.values(this.model.filters).length &&
        Object.values(this.model.filters).some(x => !!x)) ||
      this.model.query
    );
  }

  get pagedData() {
    if (this.model.loading) {
      return new Array(SKELETON_ROW_COUNT).fill({});
    }

    return paginate(
      this.isFiltered ? this.filteredData : this.tableData,
      this.model.page,
      this.model.pageLimit
    );
  }

  async loadActivities(all = false) {
    this.handleUpdate(Msg.setLoading(true));

    const response: AudienceExportActivitiesResponse =
      await audienceExportsService.loadAudienceExportActivities(all);

    if (response.success) {
      this.handleUpdate(Msg.setTableData(response.export_activities));
      this.handleUpdate(Msg.setFilteredData(response.export_activities));
      this.handleUpdate(Msg.setDataForFilters());
      this.handleUpdate(Msg.resetFilters());
      this.handleUpdate(Msg.setLoading(false));
      this.requestUpdate();
    } else {
      this.handleUpdate(Msg.setLoading(false));
      this.requestUpdate();
    }
  }

  updateResults() {
    let workingResults = this.model.tableData;

    // Filter via search text, if exists
    if (this.model.query) {
      workingResults = workingResults.filter(activity =>
        fuzzyMatch(
          safeToGrep(this.model.query),
          safeToGrep(this.haystack(activity))
        )
      );
    }

    // Filters via selectboxes

    if (this.model.filters.export_state) {
      workingResults = workingResults.filter(
        activity =>
          activity.export_state.state === this.model.filters.export_state
      );
    }

    if (this.model.filters.destination) {
      workingResults = workingResults.filter(
        activity => activity.destination_name === this.model.filters.destination
      );
    }

    if (this.model.filters.audience) {
      workingResults = workingResults.filter(
        activity => activity.audience_name === this.model.filters.audience
      );
    }

    this.handleUpdate(Msg.setFilteredData(workingResults));
    this.requestUpdate();
  }

  renderFilters() {
    return html`
      <fly-select
        placeholder="Audience"
        variant=${SelectVariant.CONDENSED}
        @selected=${(e: CustomEvent<CustomSelectEvent>) => {
          this.handleUpdate(Msg.setAudienceFilter(e.detail.value));
          this.updateResults();
        }}
        searchable
      >
        ${Object.values(this.model.audiences).map(audience => {
          return html`
            <fly-list-item value="${audience}">${audience}</fly-list-item>
          `;
        })}
      </fly-select>

      <fly-select
        placeholder="Destination"
        variant=${SelectVariant.CONDENSED}
        @selected=${(e: CustomEvent<CustomSelectEvent>) => {
          this.handleUpdate(Msg.setDestinationFilter(e.detail.value));
          this.updateResults();
        }}
        searchable
      >
        ${Object.values(this.model.destinations).map(destination => {
          return html`
            <fly-list-item value="${destination}">${destination}</fly-list-item>
          `;
        })}
      </fly-select>

      <fly-select
        placeholder="Export State"
        variant=${SelectVariant.CONDENSED}
        @selected=${(e: CustomEvent<CustomSelectEvent>) => {
          this.handleUpdate(Msg.setExportStateFilter(e.detail.value));
          this.updateResults();
        }}
      >
        ${Object.values(this.model.export_states).map(status => {
          return html`
            <fly-list-item value="${status}">${status}</fly-list-item>
          `;
        })}
      </fly-select>

      <fly-switch
        style="--fly-form-field-flex-direction: row-reverse; --fly-form-field-gap: 0;"
        label="Include Inactive Exports"
        @change=${(e: CustomEvent) => {
          this.loadActivities(e.detail.on);
        }}
      ></fly-switch>
    `;
  }

  renderCallOuts() {
    if (this.isFiltered && !this.model.filteredData.length) {
      const intentionText = this.model.query ? 'your search' : 'your selection';

      return html`
        <div class="activities-table__call-out-wrapper">
          <div
            class="activities-table__call-out activities-table__call-out--no-results"
          >
            <fly-icon icon="error"></fly-icon>
            There are no activities matching ${intentionText}.
          </div>
        </div>
      `;
    }
  }

  skeletonHeadline() {
    return exportActivitiesLocale.en.activitiesTable.noMatches.headline;
  }

  skeletonCaption() {
    return exportActivitiesLocale.en.activitiesTable.noMatches.caption;
  }

  renderSkeletonTable() {
    return html`
      <fly-call-out
        .headline=${this.skeletonHeadline()}
        .caption=${this.skeletonCaption()}
      >
      </fly-call-out>
    `;
  }

  renderHeader() {
    return html`
      <thead>
        <tr class="fly-table__columns">
          <th>
            <div class="fly-table__column fly-table__cell">Audience</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Destination</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Export State</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Export Time</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Add count</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Remove count</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Batch count</div>
          </th>
          <th>
            <div class="fly-table__column fly-table__cell">Export ID</div>
          </th>
        </tr>
      </thead>
    `;
  }

  renderLoadingRow() {
    return html`<tr class="fly-table__row ">
      ${[...Array(8)].map(() => {
        return html`<td class="fly-table__cell__loading">
          <fly-bar-loader width="100px" height="30px" animated></fly-bar-loader>
        </td>`;
      })}
    </tr>`;
  }

  renderRow(activity: ExportActivitySummary) {
    if (this.model.loading) return this.renderLoadingRow();

    const src = new URL(activity.destination_icon, window.location.origin);
    return html`
      <tr class="fly-table__row">
        <td
          @click=${(evt: CustomEvent) => {
            evt.preventDefault();
            evt.stopPropagation();
            if (!activity.dataset_group_id || !activity.audience_id) return;

            const url = activity.legacy
              ? `/dataset_groups/${activity.dataset_group_id}/audiences/${activity.audience_id}`
              : `/v2/dataset_groups/${activity.dataset_group_id}/audiences/${activity.audience_id}`;
            location.href = url;
          }}
        >
          <div class="fly-table__cell">${activity.audience_name}</div>
        </td>
        <td>
          <div class="fly-table__cell__center">
            <fly-tooltip content="${activity.destination_name}">
              <fly-icon svg icon=${src} svgIconSize=${32}></fly-icon>
            </fly-tooltip>
          </div>
        </td>
        <td>
          <div class="fly-table__cell" style="min-width: 150px;">
            <fly-tooltip content="${activity.export_state.description}">
              ${activity.export_state.state}
            </fly-tooltip>
          </div>
        </td>
        <td>
          <div class="fly-table__cell" style="min-width: 300px;">
            ${formatTimestamp(activity.time)}
          </div>
        </td>
        <td>
          <div class="fly-table__cell">${activity.successful_add_count}</div>
        </td>
        <td>
          <div class="fly-table__cell">${activity.successful_remove_count}</div>
        </td>
        <td>
          <div class="fly-table__cell">${activity.successful_batch_count}</div>
        </td>
        <td>
          <div class="fly-table__cell">
            ${this.renderExportIdCell(activity)}
          </div>
        </td>
      </tr>
    `;
  }

  renderActivitiesTable() {
    return html`
      <div class="fly-table-container" id="${this.key}">
        <div
          class="fly-table-container__controls
            fly-table-container__controls--padded data-table__header"
        >
          <div class="fly-table__controls">
            <fly-input
              hasicon
              type="search"
              placeholder="Search"
              variant=${FlyInputVariant.BORDERLESS_NO_BG}
              @input=${(e: CustomEvent<CustomInputEvent>) => {
                this.handleUpdate(Msg.setSearchQuery(e.detail.value));
                this.updateResults();
              }}
            >
              <div slot="icon">
                <fly-icon icon="search"></fly-icon>
              </div>
            </fly-input>
            <div class="activities-table__controls__filters">
              ${this.renderFilters()}
            </div>
          </div>
          <slot name="header"></slot>
        </div>
        <div
          class=${classMap({
            'fly-table': true,
            'fly-table--horizontally-scrolled': this.model.horizontallyScrolled,
          })}
          @scroll=${(e: CustomEvent) => this.handleUpdate(Msg.scrollTable(e))}
        >
          <table>
            ${this.renderHeader()}
            <tbody>
              ${repeat(
                this.pagedData,
                (activity: ExportActivitySummary) => activity.id,
                this.renderRow.bind(this)
              )}
            </tbody>
          </table>
          ${this.renderCallOuts()}
        </div>
        <div class="fly-table-container__controls data-table__footer">
          <fly-paginator
            .page=${this.model.page}
            total=${this.totalPages}
            @previous=${() => this.handleUpdate(Msg.goToPrevPage())}
            @next=${() => this.handleUpdate(Msg.goToNextPage())}
          >
          </fly-paginator>
        </div>
      </div>
    `;
  }

  renderExportIdCell(activity: ExportActivitySummary) {
    return html`<td>
      <fly-tooltip content=${activity.export_run_id.value}>
        <div
          class="fly-table__cell activities-show-table__export-id"
          @click=${() => {
            renderNotification(
              `Copied ${activity.export_run_id.value} to clipboard`,
              AlertIntent.PRIMARY,
              AlertSize.MD
            );
            copy(activity.export_run_id.value);
          }}
        >
          <div>${activity.export_run_id.short_version}</div>
          <fly-icon
            class="activities-show-table__fly-icon"
            icon="content_copy"
            size="xs"
          ></fly-icon>
        </div>
      </fly-tooltip>
    </td>`;
  }

  render() {
    return this.model.tableData?.length || this.model.loading
      ? this.renderActivitiesTable()
      : this.renderSkeletonTable();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [AUDIENCE_EXPORT_ACTIVITIES_TABLE_ELEMENT_TAG]: AudienceExportActivitiesTable;
  }
}
